/**
 * The link a Quote's Send mails, and the page that answers it with no login
 * (decision 2650; T1247): the link's guards, where a link stands, what the
 * page is told, the checks on what it sends back, and the sentences the
 * Quote's page, its History and the rep's notice read. DOM-free, so the API
 * and the page read one set of rules.
 */
import { fill, t } from '../strings';
import { counted } from '../composites/countedUnit';

/** 🔒 The token's length in random bytes; only its SHA-256 is stored. */
export const QUOTE_LINK_TOKEN_BYTES = 32;
/** 🔒 Sixty requests an hour from one client address over the four routes; the sixty-first answers an empty 429. */
export const QUOTE_LINK_PER_ADDRESS_PER_HOUR = 60;
/** 🔒 Two thousand an hour from everywhere, the ceiling beside the per-address limit, as the website intake has one. */
export const QUOTE_LINK_PER_HOUR = 2000;
/** The web page the link opens, outside the shell. */
export const QUOTE_RESPONSE_ROUTE = '/quote-response';
/** The four public routes' root, relative to the API root. */
export const PUBLIC_QUOTE_LINKS_PATH = '/public/quote-links';
/** 🔒 The longest full name the page takes, and the longest reason a decline carries. */
export const QUOTE_LINK_NAME_MAX = 120;
export const QUOTE_LINK_REASON_MAX = 1000;

/** The address the mail carries. */
export function quoteLinkUrl(appBaseUrl: string, token: string): string {
  return `${appBaseUrl.replace(/\/$/, '')}${QUOTE_RESPONSE_ROUTE}/${token}`;
}

/** Where a link stands, which decides whether the page offers Accept and Decline. */
export type QuoteLinkState = 'open' | 'accepted' | 'declined' | 'expired' | 'void' | 'superseded' | 'closed';

/**
 * 🔒 Where a link stands (decision 2650): a link a later send replaced, or
 * whose revision is no longer the Quote's, is superseded first; then the
 * Quote's own answer; then the good-till date; else open. A link already used
 * on a Quote still open reads closed, which only a store that failed after the
 * claim can leave.
 */
export function quoteLinkState(
  link: { revision: number; usedAt: Date | string | null; expiresAt: Date | string; newest: boolean },
  quote: { status: string; revision: number; expiryDate: Date | string },
  now: Date,
): QuoteLinkState {
  if (!link.newest || link.revision !== quote.revision) return 'superseded';
  if (quote.status === 'Accepted') return 'accepted';
  if (quote.status === 'Declined') return 'declined';
  if (quote.status === 'Void') return 'void';
  if (quote.status !== 'Sent' && quote.status !== 'ClientViewed') return 'closed';
  if (new Date(link.expiresAt).getTime() < now.getTime() || new Date(quote.expiryDate).getTime() < now.getTime()) return 'expired';
  if (link.usedAt) return 'closed';
  return 'open';
}

/** One line as the public page draws it: no account, no cost, no category. */
export interface PublicQuoteLine {
  id: string;
  lineNumber: number;
  description: string;
  quantity: number;
  unitName: string;
  unitPriceCents: number;
  amountCents: number;
  taxCents: number;
  optional: boolean;
}

/** 🔒 Everything the public page is told (decision 2650). A superseded link is told the number and its state alone. */
export interface PublicQuoteAnswer {
  state: QuoteLinkState;
  number: string;
  organizationName: string;
  companyName: string;
  quoteDate: string;
  expiryDate: string;
  currencyCode: string;
  lines: PublicQuoteLine[];
  /** Who accepted and on what day, where the Quote stands Accepted; null otherwise. */
  acceptedBy: { name: string; on: string } | null;
}

/**
 * 🔒 Text a customer typed on the public page, made one plain line (T1960): control characters (a line break, a tab) read as a
 * space, format characters (a right-to-left override, a zero-width mark) are dropped, and runs of whitespace collapse to one space.
 * The name rides the notice mail's Subject and the reason rides its body and the History, so neither can carry a line break
 * into a header or hide behind a direction mark.
 */
export function quoteLinkTyped(text: string): string {
  return text.replace(/\p{Cc}/gu, ' ').replace(/\p{Cf}/gu, '').replace(/\s+/g, ' ').trim();
}

/** 🔒 The typed full name Accept asks for: required, and no longer than the cap, measured once it is made one plain line. Null where it holds. */
export function quoteLinkNameProblem(name: unknown): string | null {
  if (typeof name !== 'string') return t('quoteLink.accept.needsName');
  const typed = quoteLinkTyped(name);
  // Counted in code points, as the Parts deal's part name is, so a surrogate pair is one character (T2002).
  const length = [...typed].length;
  if (length < 2) return t('quoteLink.accept.needsName');
  if (length > QUOTE_LINK_NAME_MAX) return t('quoteLink.accept.nameTooLong');
  return null;
}

/** 🔒 The reason a decline carries: optional, and no longer than the cap, measured once it is made one plain line. */
export function quoteLinkReasonProblem(reason: unknown): string | null {
  if (reason === undefined || reason === null) return null;
  if (typeof reason !== 'string') return t('quoteLink.decline.reasonTooLong');
  return [...quoteLinkTyped(reason)].length > QUOTE_LINK_REASON_MAX ? t('quoteLink.decline.reasonTooLong') : null;
}

/** The sentence the page draws where a link offers no control. */
export function quoteLinkStateSentence(state: QuoteLinkState): string {
  return t(`quoteLink.state.${state}` as Parameters<typeof t>[0]);
}

/** 🔒 Who accepted a Quote, as its page reads it (decision 2650): from the link, or recorded by a person. */
export interface QuoteAcceptanceView {
  via: 'link' | 'person';
  /** The typed name on the link; the recording person's name otherwise. */
  name: string;
  /** The instant, which the page draws as a day in the viewer's zone. */
  at: string;
  /** The client address, on the link alone. */
  address: string | null;
  optionalLineIds: string[];
}

/** 🔒 The Quote page's acceptance line, the day already in the viewer's form. */
export function quoteAcceptanceSentence(view: QuoteAcceptanceView, day: string): string {
  if (view.via === 'person') return fill(t('quote.acceptance.recorded'), { name: view.name, date: day });
  return fill(t(view.address ? 'quote.acceptance.link' : 'quote.acceptance.linkNoAddress'), { name: view.name, date: day, address: view.address ?? '' });
}

/** 🔒 The acceptance's History detail: the page's sentence without the day, which the row carries itself. */
export function acceptanceHistoryDetail(via: 'link' | 'person', name: string, address: string | null): string {
  if (via === 'person') return fill(t('quote.history.recorded'), { name });
  return fill(t(address ? 'quote.history.linkAccepted' : 'quote.history.linkAcceptedNoAddress'), { name, address: address ?? '' });
}

/** 🔒 A decline from the link's History detail, with the reason the customer typed where they typed one. */
export function linkDeclinedHistoryDetail(address: string | null, reason: string | null): string {
  const head = fill(t(address ? 'quote.history.linkDeclined' : 'quote.history.linkDeclinedNoAddress'), { address: address ?? '' });
  const typed = reason ? quoteLinkTyped(reason) : '';
  return typed ? `${head}: ${typed}` : head;
}

/**
 * 🔒 The one notice the Quote's creator is mailed when the customer answers
 * from the link (decision 2650): the answer, who gave it, from where, and the
 * Quote's own page.
 */
export function quoteAnswerNotice(facts: { number: string; companyName: string; decision: 'accepted' | 'declined'; name: string | null; address: string | null; reason: string | null; optionalTaken: number; optionalTotal: number; quoteUrl: string }): { subject: string; body: string } {
  const from = facts.address ? ` from ${facts.address}` : '';
  // 🔒 Whatever the caller hands in, the typed name and reason reach the mail as one plain line each (T1960).
  const name = quoteLinkTyped(facts.name ?? '');
  const reason = quoteLinkTyped(facts.reason ?? '');
  if (facts.decision === 'accepted') {
    return {
      subject: `[${facts.number}] Accepted by ${name} from the emailed link`,
      body: [
        `${name} accepted quote ${facts.number} for ${facts.companyName} from the emailed link${from}.`,
        ...(facts.optionalTotal > 0 ? [`They took ${facts.optionalTaken} of the ${counted(facts.optionalTotal, 'optional line')}.`] : []),
        '',
        `Open the quote: ${facts.quoteUrl}`,
      ].join('\n'),
    };
  }
  return {
    subject: `[${facts.number}] Declined from the emailed link`,
    body: [
      `Quote ${facts.number} for ${facts.companyName} was declined from the emailed link${from}.`,
      ...(reason ? ['', `The reason given: ${reason}`] : []),
      '',
      `Open the quote: ${facts.quoteUrl}`,
    ].join('\n'),
  };
}
